import { get } from 'lodash';
import mapping from '../lib/mapping/mapping.json';
import { isArray } from 'lodash';
import { compact } from 'lodash';

function removeDataKeys(jsonObj: any): any {
	if (typeof jsonObj !== 'object' || jsonObj === null) {
		return jsonObj;
	}
	if (Array.isArray(jsonObj)) {
		return jsonObj.map(removeDataKeys);
	}
	const transformedObj: any = {};
	for (const key in jsonObj) {
		if (key === 'data') {
			return removeDataKeys(jsonObj[key]);
		} else {
			transformedObj[key] = removeDataKeys(jsonObj[key]);
		}
	}
	return transformedObj;
}

function removeNullDeep(obj: any): any {
	if (Array.isArray(obj)) {
		return obj.map(removeNullDeep);
	} else if (typeof obj === 'object' && obj !== null) {
		return Object.fromEntries(
			Object.entries(obj)
				.filter(([_, v]) => v !== null)
				.map(([k, v]) => [k, removeNullDeep(v)]),
		);
	} else {
		return obj;
	}
}

function mapValuesCustom<T extends Record<string, any>, U>(obj: T, fn: (value: T[keyof T]) => U): { [K in keyof T]: U } {
	const result: Partial<{ [K in keyof T]: U }> = {};
	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			result[key] = fn(obj[key]);
		}
	}
	return result as { [K in keyof T]: U };
}

function T(key: string) {
	return get(mapping, key, key);
}

function enumT(key: string) {
	return get(mapping, `AW_${key}`, key);
}

function transformValues(obj: any, transformFunc: (value: any) => any): any {
	if (Array.isArray(obj)) {
		return obj.map((item: any) => transformValues(item, transformFunc));
	} else if (typeof obj === 'object' && obj !== null) {
		return Object.keys(obj).reduce((acc: { [key: string]: any }, key: string) => {
			acc[key] = transformValues(obj[key], transformFunc);
			return acc;
		}, {});
	} else {
		return transformFunc(obj);
	}
}

function getLabelFromJSON(searchValue: string, jsonObject: any): string {
	let label = null;
	for (let key in jsonObject) {
		if (typeof jsonObject[key] === 'object') {
			label = getLabelFromJSON(searchValue, jsonObject[key]);
			if (label !== null) {
				break;
			}
		} else if (key === 'value' && jsonObject[key] === searchValue) {
			label = jsonObject['label'];
			break;
		}
	}
	return label;
}

function getValueFromJSON(searchlabel: string, jsonObject: any): string {
	let value = null;
	for (let key in jsonObject) {
		if (typeof jsonObject[key] === 'object') {
			value = getValueFromJSON(searchlabel, jsonObject[key]);
			if (value !== null) {
				break;
			}
		} else if (key === 'label' && jsonObject[key] === searchlabel) {
			value = jsonObject['value'];
			break;
		}
	}
	return value;
}

function getLabelFromDataPath(dataPath: string) {
	const dataPathParts = dataPath.split('.');
	return T(dataPathParts.length > 0 ? dataPathParts[dataPathParts.length - 1] : dataPath);
}

function getFieldNamefromDataPath(dataPath: string) {
	const dataPathParts = dataPath.split('.');
	return dataPathParts.length > 0 ? dataPathParts[dataPathParts.length - 1] : dataPath;
}

function addIndexToPath(path: string, index: number, specificPath: string) {
	const updatedPath = path.replace(specificPath, specificPath + '.' + index);
	return updatedPath;
}

function addIndexesToPath(path: string, indexes: string[], pathKeys: string[]) {
	let result = path;
	pathKeys.forEach((key, index) => {
		const regex = new RegExp(`\\b${key}\\b`);
		result = result.replace(regex, `${key}.${indexes[index]}`);
	});
	return result;
}

function isObjectEmpty(obj: any) {
	return Object.keys(obj).length === 0;
}

type ResDataItem = {
	id: number;
	attributes: {
		[key: string]: any;
	};
};
function getOptionsFromRes(resData: ResDataItem[], keyForLabel: any) {
	let result: any[];
	if (isArray(keyForLabel)) {
		result = resData.map((ele) => ({
			label: keyForLabel.map((key: string) => get(ele, `attributes.${key}`)).join(' - '),
			value: ele.id,
		}));
	} else {
		result = resData.map((ele) => {
			if (ele.attributes[keyForLabel] && ele.id) {
				return { label: ele.attributes[keyForLabel], value: ele.id };
			}
		});
	}

	return compact(result);
}

function assignIdsToStruc(struc: any[], prefix = ''): any[] {
	return struc.map((ele, i) => {
		const id = prefix ? `${prefix}.${i + 1}` : `${i + 1}`;
		const newEle = { ...ele, id };
		if (ele.children) {
			newEle.children = assignIdsToStruc(ele.children, id);
		}
		return newEle;
	});
}

function getKostenKategorieArt(kategorieId: any, kostenKategorie: any) {
	let id: number = -1;
	if (typeof kategorieId === 'object') {
		id = get(kategorieId, 'id');
	} else {
		id = parseInt(kategorieId);
	}
	const matchingObj = kostenKategorie.find((ele: any) => get(ele, 'id') === id);
	return get(matchingObj, 'attributes.Allgemein_Kostenart_Finanzierung');
}
function groupKostenByPosition(kostenKategories: any[]): Record<string, string[]> {
	return kostenKategories.reduce(
		(acc, kategory) => {
			const position = kategory.attributes.Allgemein_Kostenposition;
			const finanzierung = kategory.attributes.Allgemein_Kostenart_Finanzierung;

			if (!acc[position]) {
				acc[position] = [];
			}

			if (!acc[position].includes(finanzierung)) {
				acc[position].push(finanzierung);
			}

			return acc;
		},
		{} as Record<string, string[]>,
	);
}

function isTrue(args: boolean | string) {
	return args === 'true' || args === true;
}

export {
	removeDataKeys,
	removeNullDeep,
	mapValuesCustom,
	T,
	enumT,
	transformValues,
	getLabelFromJSON,
	getValueFromJSON,
	getLabelFromDataPath,
	getFieldNamefromDataPath,
	addIndexToPath,
	addIndexesToPath,
	getOptionsFromRes,
	isObjectEmpty,
	assignIdsToStruc,
	getKostenKategorieArt,
	groupKostenByPosition,
	isTrue,
};
